import React, { createContext, useContext, useState, useEffect } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { LoginRequest, LoginResponse } from '../types';
import api from '../api/client';
import toast from 'react-hot-toast';

interface AuthContextData {
  user: LoginResponse | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  logout: () => void;
  updateUser: (data: Partial<Pick<LoginResponse, 'nome' | 'email'>>) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode; queryClient: QueryClient }> = ({ children, queryClient }) => {
  const [user, setUser] = useState<LoginResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser) as LoginResponse;
      if (!isTokenExpired(userData.token)) {
        setUser(userData);
      } else {
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await api.post<LoginResponse>('/usuarios/login', credentials);
      const userData = response.data;

      queryClient.clear();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      toast.success(`Bem-vindo, ${userData.nome}!`);
      return userData;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erro ao fazer login';
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    queryClient.clear();
    setUser(null);
    localStorage.removeItem('user');
    toast.success('Logout realizado com sucesso');
  };

  const updateUser = (data: Partial<Pick<LoginResponse, 'nome' | 'email'>>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...data };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        updateUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
